import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Popper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {
  DateCalendar,
  LocalizationProvider,
  MultiSectionDigitalClock,
} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import {t} from 'i18next';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './ArrDatePicker.scss';
import ArrButton from '../arr-button/ArrButton';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import {
  convertToSelectedDate,
  isEmpty,
  rangeRegex,
} from '../../../utils/formatters';
import {ReactComponent as CalendarTodayIcon} from '../../../../src/assets/images/calendar-icon.svg';
import {
  conditions,
  conditionToTextKey,
  gridDateOperatorObj,
} from '../../../shared/constants';
import {Condition, SelectedDateType} from '../../../shared/types';
import {ArrDatePickerProps} from '../../../shared/interfaces';
import {AccessTime, ErrorOutlineRounded} from '@mui/icons-material';
import {ReactComponent as ClearIcon} from '../../../../src/assets/images/filter-clear-icon.svg';
import {ReactComponent as CaretIcon} from '../../../../src/assets/images/icon-caret-transparent.svg';
import TrapFocus from '@mui/material/Unstable_TrapFocus';

type popperPlacement = 'top-start' | 'bottom-start' | 'top-end' | 'bottom-end';
type Align = 'start' | 'end';
const MenuProps = {
  PaperProps: {
    classes: {
      root: 'arr-conditions-menu-props',
    },
  },
  disableScrollLock: true,
};

const escapeReg = (s: string) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const buildAllowedRegex = (format: string) => {
  const seps = [
    ...new Set([...format].filter((ch) => /[^a-zA-Z0-9]/.test(ch))),
  ];
  const esc = seps.map(escapeReg).join('');
  // eslint-disable-next-line security/detect-non-literal-regexp
  return new RegExp(`^[0-9${esc}]*$`);
};

const ArrDatePicker = forwardRef<any, ArrDatePickerProps>(
  (
    {
      type,
      popoverProps,
      dateFormat = 'YYYY/MM/DD',
      timeFormat = 'HH:mm:ss A',
      selectedDate = [],
      selectedDateCondition = 'Is',
      onCancelClick,
      onClearClick,
      inputClass,
      inputType = 'text',
      excludeConditions = ['Not set'],
      dataTestId,
      label,
      placeholder,
      onChange,
      onRangeChange,
      disableFuture = false,
      disablePast = false,
      defaultError = true,
      required = false,
      applyEnable = false,
      showToday = false,
      shouldShowClearButton = false,
      initialStartDate = '',
      initialEndDate = '',
      fieldVariant = 'filled',
      mode = 'date',
      ampm = true,
      minDateTime,
      maxDateTime,
      disabled = false,
      helperText,
      isError = false,
      showDaysOutsideCurrentMonth = true,
      customError = '',
      onFocusOpenDatePicker = false,
      placement: popperPopupPlacement,
      popperAlign,
      anchorOverrideRef,
    },
    ref
  ) => {
    const selectedStartDate: SelectedDateType = convertToSelectedDate(
      initialStartDate,
      dateFormat
    );
    const selectedEndDate: SelectedDateType = convertToSelectedDate(
      initialEndDate,
      dateFormat
    );

    const defaultInputValue = isEmpty(initialEndDate)
      ? initialStartDate
      : `${initialStartDate}${initialEndDate}`;

    const innerRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) ?? innerRef;
    const popperRef = useRef<HTMLDivElement | null>(null);
    const lastTriggerRef = useRef<HTMLElement | null>(null);
    const shouldRestoreFocus = useRef(false);
    const [condition, setCondition] = useState<Condition | undefined>(
      selectedDateCondition
    );
    const [startDate, setStartDate] = useState<Dayjs | null>(selectedStartDate);

    const [endDate, setEndDate] = useState<Dayjs | null>(selectedEndDate);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [placement, setPlacement] = useState<popperPlacement>('bottom-start');

    const [inputError, setInputError] = useState<string>('');
    const [error, setError] = useState<boolean>(defaultError);
    const [inputValue, setInputValue] = useState<string | undefined>(
      defaultInputValue
    );

    const isQueryDatePicker = useMemo(
      () => type === 'query-date-picker',
      [type]
    );
    const isApplied = useRef<boolean>(false);
    const calendarView = useRef<string>('');
    const containerRef = useRef<HTMLDivElement>(null);

    const isConditionSelected = useMemo(() => !isEmpty(condition), [condition]);

    const visibleConditionOptions = useMemo(() => {
      return conditions.filter((item) => !excludeConditions?.includes(item));
    }, [excludeConditions]);

    useEffect(() => {
      if (type === 'date-picker') {
        if (initialStartDate != '' && startDate === null) {
          setStartDate(selectedStartDate);
        }
      } else if (
        type === 'date-range-picker' &&
        (startDate === null || endDate === null) &&
        (initialStartDate != '' || initialEndDate != '')
      ) {
        setError(true);
        setInputError(t('common.arrDatePicker.rangeFormatError'));
      }
    }, [initialEndDate, initialStartDate]);

    useEffect(() => {
      setCondition(selectedDateCondition);
    }, [selectedDateCondition]);

    const resetDate = useCallback(() => {
      setStartDate(null);
      setEndDate(null);
      onClearClick?.(condition);
      setInputValue('');
      setInputError('');
      setError(false);
    }, [condition, onClearClick]);

    const handleConditionChange = useCallback(
      (event: SelectChangeEvent) => {
        if (!isConditionSelected) {
          setStartDate(null);
          setEndDate(null);
        }
        setCondition(event.target.value as Condition);
      },
      [isConditionSelected]
    );

    const handleStartDateChange = useCallback((date: Dayjs | null) => {
      if (date) setStartDate(date);
    }, []);

    const handleEndDateChange = useCallback((date: Dayjs | null) => {
      if (date) setEndDate(date);
    }, []);

    const shouldShowRangeCalendar = useMemo(
      () =>
        type === 'date-range-picker' ||
        (isQueryDatePicker && condition === 'Between'),
      [type, isQueryDatePicker, condition]
    );

    const getStartDate = (
      arg: Dayjs | null | React.MouseEvent | undefined,
      applyEnable: boolean,
      startDate: Dayjs | null,
      dateFormat: string
    ): {start: string | undefined; date: Dayjs | null} => {
      if (applyEnable) {
        return {start: startDate?.format(dateFormat), date: startDate};
      }

      if (dayjs.isDayjs(arg)) {
        return {start: arg.format(dateFormat), date: arg};
      }

      return {start: undefined, date: null};
    };

    const getDateValue = (
      start: string | undefined,
      end: string | undefined,
      shouldShowRange: boolean
    ): string | undefined => {
      if (!start) return undefined;
      return shouldShowRange && end ? `${start} - ${end}` : start;
    };

    const handleTodayClick = useCallback(() => {
      const today = dayjs().startOf('day');
      calendarView.current = 'day';

      setStartDate(today);
      setInputValue(today.format(dateFormat));
      onChange?.(today.format(dateFormat));
      onRangeChange?.(today.format(dateFormat), today.format(dateFormat));
      setAnchorEl(null);
      // focus back to the input after popper closes
      requestAnimationFrame(() => inputRef.current?.focus());
    }, [dateFormat, onChange, onRangeChange]);

    const handleApply = useCallback(
      (arg?: Dayjs | null | React.MouseEvent) => {
        if (calendarView.current === 'year') {
          return;
        }
        isApplied.current = true;
        setError(false);
        setInputError('');
        const effectiveStartDate =
          !applyEnable && dayjs.isDayjs(arg) ? arg : startDate;
        if (mode === 'time') {
          if (!effectiveStartDate) {
            setInputError(t('common.arrDatePicker.timeRequired'));
            setError(true);
            return;
          }
          const formattedTime = effectiveStartDate.format(timeFormat);
          setInputValue(formattedTime);
          onChange?.(formattedTime);
          setAnchorEl(null);
          requestAnimationFrame(() => inputRef.current?.focus());
          return;
        }
        const {start, date} = getStartDate(
          arg,
          applyEnable,
          startDate,
          dateFormat
        );
        if (!applyEnable && date) {
          setStartDate(date);
        }

        if (mode === 'date-time') {
          if (!effectiveStartDate) {
            setInputError(t('common.arrDatePicker.dateTimeRequired'));
            setError(true);
            return;
          }

          const formattedStart = effectiveStartDate.format(dateFormat);
          let formattedEnd = '';

          if (shouldShowRangeCalendar) {
            if (endDate) {
              formattedEnd = endDate.format(dateFormat);
            }
          }
          const dateValue = getDateValue(
            formattedStart,
            formattedEnd,
            shouldShowRangeCalendar
          );
          setInputValue(dateValue);
          if (onRangeChange) onRangeChange(formattedStart, formattedEnd);
          onChange?.(dateValue ?? '');
          setAnchorEl(null);
          requestAnimationFrame(() => inputRef.current?.focus());
          return;
        }

        const end = shouldShowRangeCalendar
          ? endDate?.format(dateFormat)
          : undefined;
        const dateValue = getDateValue(start, end, shouldShowRangeCalendar);

        setInputValue(dateValue);

        if (!isConditionSelected) {
          resetDate();
        } else {
          if (shouldShowRangeCalendar && start && end && onRangeChange) {
            onRangeChange(start, end);
          }

          if (type === 'query-date-picker') {
            onChange?.(
              [start, end],
              condition ? gridDateOperatorObj[condition] : [],
              condition
            );
          } else {
            onChange?.(dateValue ?? '');
          }
        }

        setAnchorEl(null);
        // ensure focus returns to the input after the popper closes
        requestAnimationFrame(() => inputRef.current?.focus());
      },
      [
        mode,
        applyEnable,
        condition,
        dateFormat,
        endDate,
        isConditionSelected,
        onChange,
        onRangeChange,
        resetDate,
        shouldShowRangeCalendar,
        startDate,
        type,
      ]
    );

    const handleCancel = useCallback(() => {
      onCancelClick?.();
      setAnchorEl(null);
      shouldRestoreFocus.current = true;
    }, [selectedDate.length, onCancelClick]);

    const handleClear = () => {
      isApplied.current = true;
      setAnchorEl(null);
      setStartDate(null);
      setEndDate(null);
      onChange?.('');
      onRangeChange?.('', '');
      setInputValue('');
      setInputError('');
      setError(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    };

    const handleExited = useCallback(() => {
      if (!isApplied.current && (startDate || endDate)) {
        return;
      }
      if (!isApplied.current) {
        setCondition(selectedDateCondition);

        if (mode === 'time') {
          const parseTime = (val?: string) => {
            if (!val) return null;
            const d = dayjs(val, timeFormat, true);
            return d.isValid() ? d : null;
          };

          // single time picker
          if (!shouldShowRangeCalendar) {
            setStartDate(parseTime(inputValue));
            setEndDate(null);
          }

          // range time picker
          if (shouldShowRangeCalendar && inputValue) {
            const [start, end] = inputValue.split('-').map((v) => v.trim());
            setStartDate(parseTime(start));
            setEndDate(parseTime(end));
          }
        } else {
          // existing behavior for date / datetime
          setStartDate(selectedStartDate);
          setEndDate(selectedEndDate);
        }
      }

      if (isApplied.current) {
        isApplied.current = false;
      }
    }, [
      selectedDateCondition,
      selectedEndDate,
      selectedStartDate,
      inputValue,
      mode,
      shouldShowRangeCalendar,
    ]);

    useEffect(() => {
      if (!anchorEl) {
        handleExited();
      }

      if (lastTriggerRef.current) {
        requestAnimationFrame(() => {
          popperRef.current?.focus?.();
        });
      }
    }, [anchorEl]);

    const computePlacement = useCallback(
      (el: HTMLElement | null, align: Align = popperAlign ?? 'start') => {
        if (!el) return align === 'end' ? 'bottom-end' : 'bottom-start';
        const rect = el.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceAbove = rect.top;
        const spaceBelow = viewportHeight - rect.bottom;
        const vertical =
          spaceBelow > spaceAbove && spaceBelow > 375 ? 'bottom' : 'top';
        return `${vertical}-${align}` as popperPlacement;
      },
      []
    );

    const handleClick = useCallback(() => {
      if (inputRef && 'current' in inputRef) {
        const el =
          anchorOverrideRef?.current ??
          (popperAlign === 'end' ? containerRef.current : inputRef.current);
        if (!el) return;

        const align: Align = popperAlign ?? (mode === 'time' ? 'end' : 'start');

        const finalPlacement =
          popperPopupPlacement ?? computePlacement(el, align);

        setPlacement(finalPlacement);
        lastTriggerRef.current = el;
        setAnchorEl(el);
      }
      requestAnimationFrame(() => inputRef.current?.focus());
    }, [computePlacement, popperPopupPlacement, popperAlign, mode]);

    const isApplyButtonDisabled = useMemo(() => {
      if (isConditionSelected && condition === 'Not set') return false;
      return shouldShowRangeCalendar
        ? (isEmpty(startDate) || isEmpty(endDate)) && isConditionSelected
        : isEmpty(startDate) && isConditionSelected;
    }, [
      shouldShowRangeCalendar,
      startDate,
      endDate,
      isConditionSelected,
      condition,
    ]);

    const renderConditions = useMemo(() => {
      return (
        isQueryDatePicker && (
          <Select
            fullWidth
            className={'arr-conditions-dropdown-container'}
            value={condition}
            onChange={handleConditionChange}
            MenuProps={MenuProps}
            sx={{
              width:
                type === 'date-range-picker' || condition === 'Between'
                  ? '47.5%'
                  : '100%',
            }}
            renderValue={() => (
              <Typography
                className="typo-text body3_size condition-text"
                data-testid={`arr-date-picker-query-condition-${dataTestId}`}
              >
                {condition ? t(conditionToTextKey[condition]) : ''}
              </Typography>
            )}
            data-testid={`date-filter-condition-dropdown-${dataTestId}`}
          >
            {visibleConditionOptions.map((cond, index) => (
              <MenuItem
                key={cond}
                value={cond}
                data-testid={`menu-option-${dataTestId}-${cond}`}
              >
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Typography
                    className="typo-text body3_size "
                    data-testid={`arr-date-picker-typography-text-${dataTestId}-${index}`}
                  >
                    {t(conditionToTextKey[cond])}
                  </Typography>
                  {[condition].includes(cond) && (
                    <CheckIcon
                      fontSize="small"
                      className="arr-date-check-mark"
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        )
      );
    }, [
      isQueryDatePicker,
      condition,
      handleConditionChange,
      type,
      visibleConditionOptions,
    ]);

    const renderDateRangeField = useMemo(() => {
      return shouldShowRangeCalendar ? (
        <Box className={'arr-horizontal-date-container'}>
          <Box flex={1}>
            <Box className={'arr-date-container'}>
              <Typography
                className={'arr-date-placholder'}
                data-testid={`arr-date-picker-start-date`}
              >
                {t('common.arrDatePicker.startDate')}
              </Typography>
              <Typography
                className={'arr-date-input-label date-text'}
                data-testid={`arr-date-picker-start-date-${dataTestId}`}
              >
                {startDate ? startDate.format(dateFormat) : dateFormat}
              </Typography>
            </Box>
          </Box>

          <Divider orientation="horizontal" flexItem className="arr-divider" />

          <Box flex={1} justifyContent={'flex-end'}>
            <Box className={'arr-date-container'}>
              <Typography
                className={'arr-date-placholder'}
                data-testid={`arr-date-picker-end-date`}
              >
                {t('common.arrDatePicker.endDate')}
              </Typography>
              <Typography
                className={'arr-date-input-label date-text'}
                data-testid={`arr-date-picker-end-date-${dataTestId}`}
              >
                {endDate ? endDate.format(dateFormat) : dateFormat}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : undefined;
    }, [shouldShowRangeCalendar, dateFormat, startDate, endDate]);

    const allowed = useMemo(() => buildAllowedRegex(dateFormat), [dateFormat]);

    const handleInputChange = (value: string) => {
      if (!allowed.test(value)) return;

      setInputValue(value);

      if (value.trim() === '') {
        setInputError('');
        setError(false);
        onChange?.('');
        return;
      }

      if (type === 'date-range-picker') {
        if (!rangeRegex.test(value)) {
          setError(true);
          setInputError(t('common.arrDatePicker.rangeFormatError'));
          return;
        }

        const [startStr, endStr] = value.split('-').map((s) => s.trim());
        const start = dayjs(startStr, dateFormat, true);
        const end = dayjs(endStr, dateFormat, true);

        if (!start.isValid() || !end.isValid()) {
          setError(true);
          setInputError(t('common.arrDatePicker.rangeFormatError'));
          return;
        }

        if (end.isBefore(start)) {
          setError(true);
          setInputError(t('common.arrDatePicker.rangeError'));
          return;
        }
        setError(false);
        setInputError('');
        setStartDate(start);
        setEndDate?.(end);
        onChange?.(`${start.format(dateFormat)} - ${end.format(dateFormat)}`);
        onRangeChange?.(start.format(dateFormat), end.format(dateFormat));
      } else {
        const parsed = dayjs(value, dateFormat, true);
        if (parsed.isValid()) {
          setInputError('');
          setError(false);
          setStartDate(parsed);
          onChange?.(parsed.format(dateFormat));
        } else {
          setError(true);
          onChange?.(parsed.format(dateFormat));
          setInputError(
            customError || t('common.arrDatePicker.singleDateError')
          );
        }
      }
    };

    const minTimeForDay = (selected: Dayjs | null, minDT?: Dayjs) => {
      if (!selected || !minDT) return undefined;
      return selected.isSame(minDT, 'day') ? minDT : undefined;
    };

    const maxTimeForDay = (selected: Dayjs | null, maxDT?: Dayjs) => {
      if (!selected || !maxDT) return undefined;
      return selected.isSame(maxDT, 'day') ? maxDT : undefined;
    };

    const startMinTime = useMemo(
      () => minTimeForDay(startDate, minDateTime),
      [startDate, minDateTime]
    );

    const startMaxTime = useMemo(
      () => maxTimeForDay(startDate, maxDateTime),
      [startDate, maxDateTime]
    );

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          ref={containerRef}
          className={'arr-field-popover-date-filter'}
          data-testid={dataTestId}
          onClick={() => {
            if (
              (type === 'query-date-picker' || onFocusOpenDatePicker) &&
              !disabled
            ) {
              handleClick();
            }
          }}
        >
          <TextField
            label={label}
            data-testid={`input-date-${dataTestId}`}
            className={`arr-search-input ${inputClass ?? ''} ${disabled ? 'arr-disabled-field' : ''} ${inputError ? 'arr-input-error' : ''}`}
            inputRef={inputRef}
            autoComplete={'off'}
            variant={fieldVariant}
            size={'small'}
            type={inputType}
            required={required}
            error={isError ?? ((defaultError && !!error) || !!inputError)}
            helperText={
              isError && helperText
                ? helperText
                : defaultError && error && inputError
                  ? inputError
                  : ''
            }
            onChange={(event) => {
              if (disabled) return;

              if (isEmpty(event.target.value)) {
                resetDate();
                setAnchorEl(null);
                onChange?.('');
                return;
              }
              if (type === 'query-date-picker') {
                if (inputRef && 'current' in inputRef) {
                  inputRef.current?.setSelectionRange(0, 0);
                }
              } else {
                handleInputChange(event.target.value);
              }
            }}
            onFocus={() => {
              if (type === 'query-date-picker' && !disabled) {
                if (inputRef && 'current' in inputRef) {
                  inputRef.current?.setSelectionRange(0, 0);
                }
              }
            }}
            disabled={false}
            placeholder={placeholder}
            value={inputValue}
            onKeyDown={(e) => {
              if (disabled) return;

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            slotProps={{
              htmlInput: {'data-testid': `${dataTestId}-picker`},
              input: {
                readOnly: disabled,
                endAdornment: (
                  <InputAdornment position="end">
                    {shouldShowClearButton && inputValue != '' && (
                      <ArrIconButton
                        ariaLabel={t('common.arrDatePicker.clearIcon')}
                        size="small"
                        icon={<ClearIcon className="clear-icon" />}
                        className="clear-icon-button"
                        onClick={handleClear}
                        testId="arr-date-picker-clear-icon-icon-button"
                      ></ArrIconButton>
                    )}
                    <ArrIconButton
                      ariaLabel={t('common.arrDatePicker.calenderIcon')}
                      onClick={handleClick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleClick();
                        }
                      }}
                      size="small"
                      shape="none"
                      disabled={disabled}
                      icon={
                        isError ? (
                          <ErrorOutlineRounded className="error-icon" />
                        ) : mode === 'time' ? (
                          <AccessTime className="calender-today-icon" />
                        ) : (
                          <CalendarTodayIcon className="calender-today-icon" />
                        )
                      }
                      testId={`arr-date-picker-calender-icon-icon-button-${dataTestId}`}
                    ></ArrIconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Popper
          className={'arr-date-picker-popper-filter'}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement={placement}
          data-testid={`popper-${dataTestId}`}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: ({placement}: {placement: string}) => {
                  return placement.startsWith('bottom') ? [0, 2] : [0, 8];
                },
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: [
                  'top-start',
                  'bottom-start',
                  'top-end',
                  'bottom-end',
                ],
              },
            },
            {
              name: 'preventOverflow',
              options: {boundary: 'viewport', padding: 8},
            },
          ]}
          {...popoverProps}
        >
          <ClickAwayListener onClickAway={handleCancel}>
            <TrapFocus
              open={Boolean(anchorEl)}
              disableAutoFocus
              disableEnforceFocus={false}
            >
              <Paper className="date-paper" ref={popperRef} tabIndex={-1}>
                {renderConditions}
                {renderDateRangeField}

                <Box className={'arr-calendar-container'}>
                  {(mode === 'date' || mode === 'date-time') && (
                    <DateCalendar
                      value={startDate}
                      maxDate={
                        shouldShowRangeCalendar && !isEmpty(endDate)
                          ? (endDate ?? undefined)
                          : maxDateTime
                      }
                      minDate={minDateTime}
                      onChange={
                        !applyEnable ? handleApply : handleStartDateChange
                      }
                      className={'arr-calendar'}
                      disabled={condition === 'Not set' || !isConditionSelected}
                      disableFuture={disableFuture}
                      disablePast={disablePast}
                      onViewChange={(view) => (calendarView.current = view)}
                      showDaysOutsideCurrentMonth={showDaysOutsideCurrentMonth}
                      slots={{switchViewIcon: CaretIcon}}
                      fixedWeekNumber={6}
                      yearsPerRow={4}
                      data-testid={`calendar-${dataTestId}`}
                      slotProps={{
                        previousIconButton: {'data-testid': 'previous-btn'},
                        nextIconButton: {'data-testid': 'next-btn'},
                        switchViewButton: {
                          'data-testid': `switch-view-btn-${dataTestId}`,
                          className: 'arr-calendar-switch-view-btn',
                        },
                      }}
                    />
                  )}
                  {(mode === 'time' || mode === 'date-time') && (
                    <Box className="arr-digital-timepicker">
                      <MultiSectionDigitalClock
                        value={startDate}
                        onChange={
                          !applyEnable ? handleApply : handleStartDateChange
                        }
                        disabled={
                          condition === 'Not set' || !isConditionSelected
                        }
                        className={'arr-calendar'}
                        timeSteps={{minutes: 1}}
                        ampm={ampm}
                        minTime={startMinTime}
                        maxTime={startMaxTime}
                        views={['hours', 'minutes', 'seconds']}
                        data-testid={`clock-${dataTestId}`}
                      />
                    </Box>
                  )}
                  {shouldShowRangeCalendar && (
                    <>
                      {(mode === 'date' || mode === 'date-time') && (
                        <DateCalendar
                          value={endDate}
                          minDate={startDate ?? minDateTime}
                          maxDate={maxDateTime}
                          onChange={
                            !applyEnable ? handleApply : handleEndDateChange
                          }
                          className={'arr-calendar'}
                          disabled={!isConditionSelected}
                          disableFuture={disableFuture}
                          disablePast={disablePast}
                          showDaysOutsideCurrentMonth={
                            showDaysOutsideCurrentMonth
                          }
                          fixedWeekNumber={6}
                          yearsPerRow={4}
                          data-testid={`calendar-range-${dataTestId}`}
                        />
                      )}
                      {(mode === 'time' || mode === 'date-time') && (
                        <Box className="arr-digital-timepicker">
                          <MultiSectionDigitalClock
                            value={endDate}
                            onChange={handleEndDateChange}
                            disabled={!isConditionSelected}
                            className={'arr-calendar'}
                            timeSteps={{minutes: 1}}
                            ampm={ampm}
                            minTime={minDateTime}
                            maxTime={maxDateTime}
                            views={['hours', 'minutes', 'seconds']}
                            data-testid={`range-clock-${dataTestId}`}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
                <Box className={'arr-date-footer-filter'}>
                  <ArrButton
                    variant="text"
                    size="medium"
                    onClick={handleClear}
                    dataTestId={`${dataTestId}-clear-button`}
                    className="arr-date-cancel-button-filter"
                  >
                    {t('common.arrDatePicker.cancel')}
                  </ArrButton>
                  {applyEnable && (
                    <ArrButton
                      variant="text"
                      size="medium"
                      onClick={handleApply}
                      isAriaDisabled={isApplyButtonDisabled}
                      dataTestId={`${dataTestId}-arr-date-apply-filter-button`}
                      className={`arr-date-apply-button-filter ${isApplyButtonDisabled ? 'btn-disabled' : ''}`}
                    >
                      {t('common.arrDatePicker.ok')}
                    </ArrButton>
                  )}
                  {showToday && mode !== 'time' && (
                    <ArrButton
                      variant="text"
                      size="medium"
                      onClick={handleTodayClick}
                      dataTestId={`${dataTestId}-arr-date-today-button`}
                      className="arr-date-apply-button-filter"
                    >
                      {t('common.arrDatePicker.today')}
                    </ArrButton>
                  )}
                </Box>
              </Paper>
            </TrapFocus>
          </ClickAwayListener>
        </Popper>
      </LocalizationProvider>
    );
  }
);

export default ArrDatePicker;
